import {
  getPersistedOrgId,
  readTokenOrgClaims,
  setPersistedOrgId,
  useAuth0
} from "@appretio/auth";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { TenantContext, TenantContextValue } from "./tenantContext";
import { useUserOrganizations } from "./useUserOrganizations";

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

/**
 * Provides the active tenant (Auth0 organization), the switch list, and a
 * `switchOrg` action to the app. Mount inside the Auth0, API, and React Query
 * providers (it depends on all three). Pairs the organization switcher in the
 * `UserMenu` with the redirect-based org-scoped re-login.
 */
export function TenantProvider({ children }: PropsWithChildren) {
  if (AUTH_DISABLED) {
    return <BypassTenantProvider>{children}</BypassTenantProvider>;
  }
  return <RealTenantProvider>{children}</RealTenantProvider>;
}

function RealTenantProvider({ children }: PropsWithChildren) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { data: organizations = [], isLoading } = useUserOrganizations();

  const [activeOrgId, setActiveOrgId] = useState<string | null>(
    getPersistedOrgId
  );
  const [isSwitching, setIsSwitching] = useState(false);

  // Confirm the active org against the real access-token claim once a token is
  // available, so the selector reflects what the API actually sees (the token
  // is the source of truth) rather than only the persisted guess.
  useEffect(() => {
    let cancelled = false;
    getAccessTokenSilently()
      .then((token) => {
        if (cancelled) return;
        const { orgId } = readTokenOrgClaims(token);
        if (orgId) {
          setActiveOrgId(orgId);
        }
      })
      .catch(() => {
        // Token unavailable (re-auth in progress) — keep the persisted value.
      });
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const currentOrg = useMemo(
    () => organizations.find((org) => org.id === activeOrgId) ?? null,
    [organizations, activeOrgId]
  );

  const switchOrg = useCallback(
    async (orgId: string) => {
      if (orgId === activeOrgId) return;
      setIsSwitching(true);
      // Switch via a top-level org-scoped redirect, NOT a silent token re-mint.
      // Two reasons the silent path is wrong here:
      //   1. The API's token getter calls getAccessTokenSilently() with no org
      //      override, so it resolves against the org the Auth0 client was built
      //      with at mount and cache-hits the OLD org's still-valid token — every
      //      request after a silent switch would carry the previous tenant.
      //   2. auth0-spa-js bypasses the refresh token for a different-org request
      //      and falls back to a prompt=none iframe, which needs the Auth0 session
      //      cookie in a third-party context — blocked by Safari/Firefox/3PC-off
      //      Chrome, so the switch just throws there.
      // A full-page redirect re-mounts the app scoped to the newly persisted org
      // (onRedirectCallback persists user.org_id and returns to `returnTo`), which
      // both re-scopes every token and clears stale tenant-bound state. With a live
      // Auth0 session it's a sub-second bounce with no credential prompt.
      setPersistedOrgId(orgId);
      try {
        await loginWithRedirect({
          authorizationParams: { organization: orgId },
          appState: {
            returnTo: window.location.pathname + window.location.search
          }
        });
      } catch (error) {
        // Failed to even launch the redirect — restore the prior selection so the
        // UI and persisted org stay consistent.
        setPersistedOrgId(activeOrgId);
        setIsSwitching(false);
        throw error;
      }
    },
    [activeOrgId, loginWithRedirect]
  );

  const value = useMemo<TenantContextValue>(
    () => ({
      currentOrg,
      organizations,
      isLoading,
      isSwitching,
      switchOrg
    }),
    [currentOrg, organizations, isLoading, isSwitching, switchOrg]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

// Auth-disabled dev bypass: no Auth0, no token, no selector switching. Mirrors
// the API's synthetic single-tenant dev user — a resolved bestseller org.
function BypassTenantProvider({ children }: PropsWithChildren) {
  const value = useMemo<TenantContextValue>(
    () => ({
      currentOrg: {
        id: "dev",
        name: "bestseller",
        display_name: "BESTSELLER"
      },
      organizations: [],
      isLoading: false,
      isSwitching: false,
      switchOrg: async () => {}
    }),
    []
  );
  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}
